import { useEffect, useState } from 'react';
import { fetchConsentHistory } from '../../../../common/ConsentAudit/consentAuditApiHelper';
import { ConsentHistoryEntry, ConsentType } from '../../../../common/ConsentAudit/types';

/** Latest consent time (epoch ms) per GSTIN, keyed by the upper-cased GSTIN. */
export type GstConsentDates = Record<string, number>;

// Shared reference so consumers see a stable value while no dates apply.
const NO_DATES: GstConsentDates = {};

export function latestConsentByGstin(entries: ConsentHistoryEntry[]): GstConsentDates {
    const dates: GstConsentDates = {};
    entries.forEach(({ gstin, consentedAt }) => {
        if (!gstin || !consentedAt) return;
        const key = gstin.toUpperCase();
        if (!dates[key] || consentedAt > dates[key]) dates[key] = consentedAt;
    });
    return dates;
}

/** Per-GSTIN last GST consent for the GST Connected popup; fetched once per org while enabled, silent on failure. */
export function useGstConsentDates(orgId: string | undefined, enabled: boolean): GstConsentDates {
    const [loaded, setLoaded] = useState<{ orgId: string; dates: GstConsentDates }>();

    useEffect(() => {
        setLoaded(undefined);
        if (!enabled || !orgId) return;
        let cancelled = false;
        fetchConsentHistory(
            { orgId, type: ConsentType.GST },
            {
                onSuccess: (entries: ConsentHistoryEntry[]) => {
                    if (!cancelled) setLoaded({ orgId, dates: latestConsentByGstin(entries) });
                },
                // Silent by design: on failure the popup simply shows no consent dates.
                onError: () => undefined,
            },
        );
        return () => {
            cancelled = true;
        };
    }, [orgId, enabled]);

    // Checked at render: the effect's reset only lands after a render that would show the previous org's dates.
    return enabled && loaded && loaded.orgId === orgId ? loaded.dates : NO_DATES;
}
